/// <amd-module name="Build/Admin/Common/Sources/ServiceEndpointSource" />
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";

import { AzureEndpointConstants, EndpointAuthorizationSchemes } from "ServiceEndpoints/Client/Constants/ServiceEndpoint";
import { AzureSubscription, AzureSubscriptionQueryResult, ServiceEndpoint } from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";
import { getServiceEndpointClient, IServiceEndpointRestClient } from "ServiceEndpoints/Client/RestClient/ServiceEndpoint";

import { VirtualMachineScaleSet } from "Build/Admin/Common/Client/Contracts/DistributedTask/Azure";
import { VirtualMachineScaleSetData } from "Build/Admin/Common/Client/Contracts/DistributedTask/DataProvider";
import { DataProviders, ServiceEndpointConstants } from "Build/Admin/Common/Constants";

import * as Resources from "Build/Admin/Common/Resources";

export class ServiceEndpointSource {
    private _pageContext: IVssPageContext;
    private _serviceEndpointRestClient: IServiceEndpointRestClient;

    private _sleepTimeAwaitingEndpoint: number = 1000; // done this way for l0 tests to manipulate the value

    constructor(pageContext: IVssPageContext) {
        this._pageContext = pageContext;
        this._serviceEndpointRestClient = getServiceEndpointClient(pageContext);
    }

    public async getAzureSubscriptions(): Promise<AzureSubscription[]> {
        return this._serviceEndpointRestClient.getAzureSubscriptions().then((queryResult: AzureSubscriptionQueryResult) => {
            return queryResult.value;
        });
    }

    public async getServiceEndpoints(projectId: string): Promise<ServiceEndpoint[]> {
        let connectionType: string = AzureEndpointConstants.AzureRmEndpointType;
        let authSchemes: string[] | undefined = undefined;
        if (connectionType.indexOf(":") > 0) {
            const splitString: string[] = connectionType.split(":");
            connectionType = splitString[0];
            authSchemes = splitString[1].split(",");
        }

        const endPointIds = undefined;
        const includeFailed = undefined;
        return this._serviceEndpointRestClient.getServiceEndpoints(projectId, connectionType, authSchemes, endPointIds, includeFailed);
    }

    public async createAzureRMConnection(projectId: string, subscription: AzureSubscription): Promise<ServiceEndpoint> {
        // reference https://dev.azure.com/mseng/AzureDevOps/_git/AzureDevOps?path=%2FServiceEndpoints%2FSdk%2FServer%2FAzureEndpointConstants.cs&version=GBmaster
        // until these contants are automatically part of the ServiceEndpoints client

        const endpoint: ServiceEndpoint = {
            authorization: {
                parameters: {
                    authenticationType: AzureEndpointConstants.SpnKeyAuthType,
                    tenantId: subscription.subscriptionTenantId
                },
                scheme: EndpointAuthorizationSchemes.ServicePrincipal
            },
            data: {
                creationMode: AzureEndpointConstants.SpnCreationModeAutomatic,
                environment: AzureEndpointConstants.DefaultAzureEnvironment,
                scopeLevel: AzureEndpointConstants.SubscriptionString,
                subscriptionId: subscription.subscriptionId,
                subscriptionName: subscription.displayName
            },
            type: ServiceEndpointConstants.AzureRMConnectionType,
            name: subscription.displayName + '(' + subscription.subscriptionId + ')',
            serviceEndpointProjectReferences: [
                {
                    name: subscription.displayName + '(' + subscription.subscriptionId + ')',
                    projectReference: {
                        id: projectId
                    }
                }
            ]
        } as any as ServiceEndpoint;

        const serviceEndpoint: ServiceEndpoint = await this._serviceEndpointRestClient.createServiceEndpoint(endpoint);

        return this._returnEndpointWhenReady(projectId, serviceEndpoint.id);
    }

    public async getVirtualMachineScaleSetsAsync(projectId: string, serviceEndpoint: ServiceEndpoint): Promise<VirtualMachineScaleSet[]> {
        const parameters: { [key: string]: any } = {
            serviceEndpointId: serviceEndpoint.id,
            serviceEndpointScope: projectId,
            mode: "getVirtualMachineScaleSets"
        };

        const contributionService: IVssContributionService = this._pageContext.getService<IVssContributionService>("IVssContributionService");
        return contributionService.getDataAsync<VirtualMachineScaleSetData>(DataProviders.ElasticPool, parameters, true).then(
            (vmssData: VirtualMachineScaleSetData) => {
                if (vmssData) {
                    if (vmssData.errorMessage) {
                        return Promise.reject(new Error(vmssData.errorMessage));
                    }
                    else {
                        return vmssData.virtualMachineScaleSets;
                    }
                }
                else {
                    return Promise.reject(new Error(Resources.GetVirtualMachineScaleSetDataError));
                }
            }
        );
    }

    /**
     * We call this after creating an AzureRM connection. The call to create the service connection succeeds right away
     * but the connection isn't actually ready or successful at this point. The only way I've found to know that the
     * connection is ready is polling. So, after creating the connection we poll for 2 minutes; each time testing
     * the connection to find out if it returns 'isReady'
     *
     * While polling, we sleep in 1 second intervals to not hammer the server and to not block the UI as you can still
     * interact with the form or yaml editor while waiting for this to finish.
     *
     * If this times out or there is an error, we post that error against the input that caused this creation in the
     * first place (should be an AzureSubscriptionPickList) so that it can potentially handle displaying the
     * error of what happened and allow the user to pick a different subscription or retry.
     */
    private async _returnEndpointWhenReady(projectId: string, endpointId: string): Promise<ServiceEndpoint> {
        let endpointDetails: ServiceEndpoint = await this._serviceEndpointRestClient.getServiceEndpointDetails(projectId, endpointId);
        const maxIterations = 120;
        let currentIteration = 0;
        while (!endpointDetails.isReady) {
            if (endpointDetails.operationStatus && endpointDetails.operationStatus.state === "Failed") {
                throw new Error(endpointDetails.operationStatus.statusMessage);
            }

            if (currentIteration >= maxIterations) {
                throw new Error(Resources.CreateRMTimeout);
            }
            currentIteration++;

            await this._sleep(this._sleepTimeAwaitingEndpoint);

            endpointDetails = await this._serviceEndpointRestClient.getServiceEndpointDetails(projectId, endpointId);
        }

        return endpointDetails;
    }

    private async _sleep(ms: number) {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }
}
